/**
 * summary-stats.js
 */

import React from "react";
import { useTheme, alpha } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import {
  Box,
  CardHeader,
  CardContent,
  Typography,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2'; 
import FeatureBox from 'components/feature-box';
import Card from 'components/card';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
);


const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
  },
  cardContent: {
    height: theme.spacing(46),
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  numbers: {
    padding: `${theme.spacing(2)} 20px ${theme.spacing(3)} 20px`,
  },
  titleLeft: {
    fontSize: theme.typography.sizes[4],
    fontWeight: 600,
  },
  titleRight: {
    fontSize: theme.typography.sizes[2],
  },
  stat: {
    minWidth: theme.spacing(10),
  },
  bigNumber: {
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
    color: theme.palette.sys.primary
  },
  label: {
    lineHeight: 1.3,
    fontSize: theme.typography.sizes[2],
    maxWidth: theme.spacing(15),
    textAlign: 'center'
  },
  headerRoot: {
    alignItems: 'flex-start',
    height: `calc(${theme.spacing(6.5)} + 2px)`,
    overflow: 'hidden',
    padding: 0,
    borderBottom: `1px solid ${theme.palette.sys.outlineVariant}`,   
  },
  headerAnimate: {
    cursor: 'pointer', 
    transition: `${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut}`,
    '&:hover': {
      backgroundColor: theme.palette.sys.primaryContainer,
      '& $headerContent': {
        top: `calc(0px - ${theme.spacing(6.5)} - 2px)`,
      }
    }
  },
  headerContent: {
    transition: `${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut}`,
    position: 'relative',
    top: 0,
  },
  headerTitle: {
    padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  }
}));

export default function SummaryStats(props) {
  const classes = useStyles();
  const theme = useTheme();
  const [isDefaultView, setIsDefaultView] = React.useState(true);
  const [subTitle, setSubTitle] = React.useState();
  const [stats, setStats] = React.useState();

  // update title and stats when data is received
  React.useEffect(() => {
    if (props.data && props.data.recent) {
      setSubTitle(props.data.recent.label);
      setStats(props.data.recent.numberStats);
    }
  }, [props.data]);

  // bar chart options
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (e, _, chart) => {
      const points = chart.getElementsAtEventForMode(e, 'point', {}, true);
      if (points[0]) {
        setIsDefaultView(false);
        setSubTitle(props.data.bars[points[0].index].label);
        setStats(props.data.bars[points[0].index].numberStats);
      }
    },
    onHover: (e, _, chart) => {
      const points = chart.getElementsAtEventForMode(e, 'point', {}, true);
      if (points.length) {
        e.native.target.style.cursor = 'pointer';
      } else {
        e.native.target.style.cursor = 'default';
      }
    },
    plugins: {
      title: {
        text: props.chartLabel,
        display: true,
        font: {size: 16, style: 'normal', weight: 'normal'},
        color: theme.palette.sys.onSurface,
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.grey.colors[9], 0.8),
        titleColor: theme.palette.primary.colors[2],
        bodyColor: theme.palette.primary.colors[3],
        borderColor: alpha(theme.palette.primary.colors[3], 0.5),
        borderWidth: 1,
        displayColors: false,
        position: 'custom',
        callbacks: {
          title: (() => ''),
          label: ((context) => {
            const index = context.dataIndex; // TODO: datasetIndex ?
            const val = context.chart.data.datasets.reduce((total, curr, i, arr) => {
              return total + curr.data[index];
            }, 0);
            return `${val} ${props.toolTipSuffix + (val === 1 ? '' : 's')}`;
          })
        },
      },
    },
    scales: {
      y: {
        stacked: true,
        grid: {
          lineWidth: 1,
          color: theme.palette.sys.outlineVariant,
          zeroLineWidth: 1,
          zeroLineColor: theme.palette.sys.outlineVariant,
          
        },
        ticks: {
          beginAtZero: true,
          display: true,
          maxTicksLimit: 5,
          color: theme.palette.sys.onSurface,
          //stepSize: props.yStepSize,
        },
      },
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          color: theme.palette.sys.onSurface,
        },
      },
    }
  };
  
  // bar chart data
  const barChartData = {
    labels: props.data && props.data.bars.map(bar => bar.label),
    datasets: props.data && props.data.datasets.map(dataset => {
      let data = props.data.bars.map(bar => bar.data[dataset.label]);
      return {
        ...dataset,
        data,
        barPercentage: 0.6,
      };
    }),
  };

  // custom tooltip position function
  Tooltip.positioners.custom = function (elements, eventPosition) {

    if (elements[0]) {
      const el = elements[0];
      const tooltip = this;
      const left = eventPosition.x <= tooltip.chart.width / 2;
      const xDiff = el.element.width / (left ? 2 : -2);
      return {
        x: el.element.x + xDiff,
        y: el.element.y,
        xAlign: left ? 'left' : 'right',
        yAlign: 'center',
      };
    }
    return {};
  };

  // reset clicked
  const resetStats = () => {
    if (props.data) {
      setIsDefaultView(true);
      setSubTitle(props.data.recent.label);
      setStats(props.data.recent.numberStats);
    }
  }

  // generate bar title elements
  const title = (
    <Box
      display="flex"
      flexDirection="column">
      <Box 
        display="flex" 
        flexDirection="row" 
        alignItems="center" 
        justifyContent="space-between" 
        className={classes.headerTitle}
      >
        <Typography variant="h6" className={classes.titleLeft}>{props.title}</Typography>
        <Typography variant="h6" className={classes.titleRight}>{subTitle}</Typography>
      </Box>
      <Box 
        display="flex"
        flexDirection="row"
        justifyContent="center"
        className={classes.headerTitle}>
        <Typography variant="h6">Reset {props.data && `to ${props.data.recent.label}`}</Typography>  
      </Box>
    </Box>
  )

  return (
    <Card
      className={classes.root}
    >
      <CardHeader
        classes={{
          root: clsx(
            classes.headerRoot, {
              [classes.headerAnimate]: !isDefaultView
            }
          ),
          content: classes.headerContent,
          title: classes.headerTitle
        }}
        disableTypography={true}
        title={title}
        onClick={resetStats}
      />
      <CardContent>
        <FeatureBox loading={!props.data} className={classes.cardContent}>
          {props.data && 
          <>
            <Box 
              display="flex" 
              flexDirection="row" 
              alignItems="flex-start" 
              justifyContent="space-between" 
              className={classes.numbers}
            >
              {stats && stats.map((stat, index) => 
                <Box
                  key={index}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  className={classes.stat}
                >
                  <Typography variant="h3" className={classes.bigNumber}>{stat.value}</Typography>
                  {stat.label.map(label => 
                    <Typography variant="caption" key={label} className={classes.label}>{label}</Typography>
                  )}                  
                </Box>
              )}            
            </Box>
            <Box>
              <Bar 
                data={barChartData} 
                options={barChartOptions}
                height={theme.spacing(24)}
              />
            </Box>
          </>}
        </FeatureBox>        
      </CardContent>
    </Card>
  );
}
